import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';
import { filter, takeUntil, skip, distinctUntilChanged } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { lastValueFrom } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { MastersService } from '../../services/masters/masters.service';
import { DynamicListingComponent, ColumnConfig, ActionConfig, ActionEvent } from '../../dynamic-listing/dynamic-listing';
import { DynamicForm } from '../../dynamic-form/dynamic-form';
import { SVG_ICONS } from '../../utils/svg-icons.utils';
import * as XLSX from 'xlsx';

@Component({
  selector: 'lib-bank-name',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    ButtonModule,
    DynamicListingComponent,
    DynamicForm
  ],
  templateUrl: './bank-name.html',
})
export class BankName implements OnInit, OnDestroy {
  masterType = 'bank_name';
  isLoading: boolean = false;
  tableData: any[] = [];
  private destroy$ = new Subject<void>();
  
  pageIndex: number = 1;
  rowsPerPage: number = 10;
  totalCount: number = 0;
  totalPages: number = 0;
  viewMode: 'list' | 'form' = 'list';
  isEditMode: boolean = false;
  selectedItemId: string | number | null = null;
  showInactives = false;
  formData: any = null;
  formGroup: FormGroup;
  columnConfigs: ColumnConfig[] = [];
  actionConfigs: ActionConfig[] = [
    {
      key: 'toggle',
      label: 'Status',
      tooltip: 'Toggle Active/Inactive',
      type: 'toggle'
    },
    {
      key: 'edit',
      label: 'Edit',
      tooltip: 'Edit',
      severity: 'secondary',
      svgIcon: SVG_ICONS.EDIT
    },
    {
      key: 'delete',
      label: 'Delete',
      tooltip: 'Delete',
      severity: 'secondary',
      svgIcon: SVG_ICONS.DELETE
    }
  ];
  formFields: any[] = [];

  constructor(
    private mastersService: MastersService,
    private router: Router,
    private route: ActivatedRoute,
    private fb: FormBuilder
  ) {
    this.formGroup = this.fb.group({});
  }

  async loadMasters(page: number = 1) {
    try {
      this.isLoading = true;
      const params: any = {};
      if (this.showInactives) {
        params.include_inactive = 'true';
      }
      const response: any = await lastValueFrom(
        this.mastersService.getBankNames(params)
      );

      const dataKey = 'bank_names';
      this.tableData = response?.data?.[dataKey] || [];
      this.totalCount = response?.data?.total || this.tableData.length;
      this.totalPages = Math.ceil(this.totalCount / this.rowsPerPage);
      this.pageIndex = page;

      if (response?.data?.references) {
        this.updateColumnsFromReferences(response.data.references);
      }
    } catch (error) {
      console.error('Error loading masters:', error);
    } finally {
      this.isLoading = false;
    }
  }

  updateColumnsFromReferences(references: any[]) {
    this.columnConfigs = references
      .map(ref => {
        let columnType: 'text' | 'date' | 'currency' | 'number' | 'status' | 'custom' = 'text';
        if (ref.type === 'datetime' || ref.type === 'date') {
          columnType = 'date';
        } else if (ref.type === 'number') {
          columnType = 'number';
        } else if (ref.type === 'boolean') {
          columnType = 'status';
        }

        return {
          key: ref.key,
          header: ref.label,
          type: columnType,
          customField: false
        };
      });
  }

  async loadItemForEdit(id: string | number) {
    try {
      this.isLoading = true;
      await this.loadFormReferences();
      const response: any = await lastValueFrom(
        this.mastersService.getBankNameById(id)
      );
      this.formData = response?.data || response;
      this.initializeForm();
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading item for edit:', error);
      alert('Failed to load item for editing');
      this.isLoading = false;
    }
  }

  async loadFormReferences() {
    try {
      const response: any = await lastValueFrom(
        this.mastersService.getReferences('bank-names')
      );
      
      if (response?.data?.references) {
        this.formFields = response.data.references.map((ref: any, index: number) => {
          let fieldType = 'input';
          if (ref.type === 'datetime' || ref.type === 'date') {
            fieldType = 'date';
          } else if (ref.type === 'number') {
            fieldType = 'number';
          } else if (ref.type === 'boolean') {
            fieldType = 'checkbox';
          } else if (ref.type === 'string' && ref.key.includes('description')) {
            fieldType = 'textarea';
          } else if (ref.type === 'label') {
            fieldType = 'label';
          }

          return {
            field_name: ref.key || ref.field_name || '',
            display_name: ref.label || ref.display_name || '',
            field_type: fieldType,
            data_type: ref.type || ref.data_type || 'string',
            is_mandatory: ref.required || ref.is_mandatory || false,
            is_visible: ref.is_visible !== undefined ? ref.is_visible : true,
            is_custom_field: ref.is_custom_field || false,
            tab_id: ref.tab_id || 1,
            sub_tab_id: ref.sub_tab_id || 0,
            display_order: ref.display_order !== undefined ? ref.display_order : index + 1,
            validation_rules: ref.validation_rules || '',
            help_text: ref.help_text || '',
            default_value: ref.default_value || '',
            is_active: ref.is_active !== undefined ? ref.is_active : true
          };
        });
      }
    } catch (error) {
      console.error('Error loading form references:', error);
    }
  }

  initializeForm() {
    const formControls: any = {};
    
    this.formFields.forEach(field => {
      const key = field.field_name.toLowerCase().replace(/[^a-z0-9]/g, '_');
      let value = this.formData?.[field.field_name] || 
                  this.formData?.[key] || 
                  this.formData?.[field.field_name.toLowerCase()] ||
                  this.formData?.[field.field_name.toLowerCase().replace(/[^a-z0-9]/g, '_')] ||
                  (field.field_type === 'checkbox' ? false : '');
      
      const validators = field.is_mandatory ? [Validators.required] : [];
      formControls[key] = [value, validators];
    });
    
    if (this.formData) {
      Object.keys(this.formData).forEach(dataKey => {
        const normalizedKey = dataKey.toLowerCase().replace(/[^a-z0-9]/g, '_');
        if (!formControls[normalizedKey]) {
          formControls[normalizedKey] = [this.formData[dataKey], []];
        }
      });
    }
    
    this.formGroup = this.fb.group(formControls);
  }

  onActionClicked(event: ActionEvent) {
    const { action, row } = event;
    
    switch (action) {
      case 'edit':
        this.editItem(row);
        break;
      case 'toggle':
        this.toggleStatus(row);
        break;
      case 'delete':
        this.deleteItem(row);
        break;
    }
  }

  editItem(row: any) {
    this.router.navigate(['/settings/masters/bank-name/edit', row.id]);
  }

  async toggleStatus(row: any) {
    const newStatus = Boolean(row.is_active);
    const statusText = newStatus ? 'activate' : 'deactivate';
    
    if (confirm('Are you sure you want to ' + statusText + ' "' + (row.name || row.id) + '"?')) {
      try {
        this.isLoading = true;
        const currentItem: any = await lastValueFrom(
          this.mastersService.getBankNameById(row.id)
        );
        
        const updateData = {
          ...(currentItem?.data || currentItem),
          is_active: newStatus
        };
        
        await lastValueFrom(
          this.mastersService.updateBankName(row.id, updateData)
        );
        
        this.loadMasters(this.pageIndex);
      } catch (error) {
        console.error('Error toggling status:', error);
        alert('Failed to toggle status');
      } finally {
        this.isLoading = false;
      }
    }
  }

  async deleteItem(row: any) {
    if (confirm('Are you sure you want to delete "' + (row.name || row.id) + '"?')) {
      try {
        await lastValueFrom(
          this.mastersService.deleteBankName(row.id)
        );
        this.loadMasters(this.pageIndex);
      } catch (error) {
        console.error('Error deleting item:', error);
        alert('Failed to delete item');
      }
    }
  }

  onPageChanged(page: number) {
    this.loadMasters(page);
  }

  onShowInactivesChange() {
    this.pageIndex = 1;
    this.loadMasters(this.pageIndex);
  }

  onCreateClick() {
    this.router.navigate(['/settings/masters/bank-name/create']);
  }

  async ngOnInit() {
    await this.checkRouteAndLoad();
    
    // Track previous URL to avoid duplicate calls
    let previousUrl = this.router.url;
    
    // Subscribe to route parameter changes
    this.route.params
      .pipe(
        skip(1), // Skip the initial emission since we already called checkRouteAndLoad above
        distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
        takeUntil(this.destroy$)
      )
      .subscribe(async () => {
        await this.checkRouteAndLoad();
      });
    
    // Subscribe to navigation events to handle URL changes (e.g., list -> create/edit)
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        takeUntil(this.destroy$)
      )
      .subscribe(async (event: any) => {
        const currentUrl = event.urlAfterRedirects || event.url;
        // Only call checkRouteAndLoad if URL actually changed
        if (currentUrl !== previousUrl) {
          previousUrl = currentUrl;
          await this.checkRouteAndLoad();
        }
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async checkRouteAndLoad() {
    const url = this.router.url;
    let routeParams = this.route.snapshot.params;
    let childRoute = this.route.snapshot.firstChild;
    while (childRoute) {
      routeParams = { ...routeParams, ...childRoute.params };
      childRoute = childRoute.firstChild;
    }
    
    let id = routeParams['id'];
    if (!id && url.includes('/edit/')) {
      const match = url.match(/\/edit\/([^\/]+)/);
      if (match) id = match[1];
    }
    if (!id && url.includes('/view/')) {
      const match = url.match(/\/view\/([^\/]+)/);
      if (match) id = match[1];
    }
    
    if (url.includes('/create') || routeParams['action'] === 'create') {
      this.viewMode = 'form';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.formData = {};
      this.isLoading = true;
      await this.loadFormReferences();
      this.initializeForm();
      this.isLoading = false;
    }
    else if (id && (url.includes('/edit') || url.includes('/view'))) {
      this.selectedItemId = id;
      this.isEditMode = url.includes('/edit');
      this.viewMode = 'form';
      await this.loadItemForEdit(id);
    }
    else {
      this.viewMode = 'list';
      this.isEditMode = false;
      this.selectedItemId = null;
      this.loadMasters();
    }
  }

  onFormFieldChanged(event: any) {
    if (!this.formData) {
      this.formData = {};
    }
    this.formData[event.key] = event.value;
  }

  async onFormSubmit() {
    if (this.formGroup.invalid) {
      this.formGroup.markAllAsTouched();
      return;
    }

    try {
      this.isLoading = true;
      const formValue = this.formGroup.value;
      
      if (this.isEditMode && this.selectedItemId) {
        await lastValueFrom(
          this.mastersService.updateBankName(this.selectedItemId, formValue)
        );
      } else {
        await lastValueFrom(
          this.mastersService.createBankName(formValue)
        );
      }
      this.router.navigate(['/settings/masters/bank-name']);
    } catch (error) {
      console.error('Error saving item:', error);
      alert('Failed to save item');
    } finally {
      this.isLoading = false;
    }
  }

  onCancel() {
    this.router.navigate(['/settings/masters/bank-name']);
  }
}
